import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { useQuery } from "@tanstack/react-query";
import {
  WorkStatus,
  CatJobHistoryItem,
  CatJobHistoryResponse,
  IntegrationStatusResult,
  CatRunStarted,
  CatRunInFlight,
  CatRunRefused,
  CatRunFailed,
} from "../../../client/types.gen";
import type { RunType } from "../../../api/routes";
import { CatRunMode } from "./types";
import {
  CatRunStatusPolling,
  POLL_INTERVAL_MS,
} from "./useCatRunStatusPolling";

type RunOutcome = CatRunStarted | CatRunInFlight | CatRunRefused | CatRunFailed;

interface JobHistoryConfig {
  fetch: (reportId: string) => Promise<CatJobHistoryResponse>;
  onUpdate: (modelType: RunType, jobs: CatJobHistoryItem[]) => void;
}

interface CatModelPollingConfig {
  modelType: RunType;
  displayName: string;
  reportId: string;
  toast: Toast;
  /** Shared across vendors, so all their pending runs poll together. */
  statusPolling: CatRunStatusPolling;
  initiateRequest: (
    reportId: string,
    activate: boolean,
    mode?: CatRunMode
  ) => Promise<RunOutcome>;
  /** Kept in sync while requests are pending. Raters have no CAT run history. */
  jobHistory?: JobHistoryConfig;
  /** Called when all pending requests complete without failure. */
  onComplete: (completedIds: string[]) => Promise<void>;
  /** If true, extract error_detail from failed statuses for the toast. */
  showErrorDetail?: boolean;
}

interface CatModelPollingResult {
  pendingRequestIds: string[];
  setPendingRequestIds: React.Dispatch<React.SetStateAction<string[]>>;
  isInitiateLoading: boolean;
  isPending: boolean;
  handleInitiate: (activate: boolean, mode?: CatRunMode) => Promise<void>;
  /** Report and poll this vendor's outcome from a request that started it. */
  applyOutcome: (outcome: RunOutcome) => Promise<void>;
}

type StatusWithId = IntegrationStatusResult & { id: string };

export function useCatModelPolling(
  config: CatModelPollingConfig
): CatModelPollingResult {
  const { modelType, statusPolling } = config;
  const { setPendingIds } = statusPolling;
  const pendingRequestIds = statusPolling.pendingIds(modelType);
  const setPendingRequestIds: React.Dispatch<React.SetStateAction<string[]>> =
    useCallback(
      (action) => setPendingIds(modelType, action),
      [setPendingIds, modelType]
    );
  const [isInitiateLoading, setIsInitiateLoading] = useState(false);

  // Keep callbacks in a ref so the query functions always see the
  // latest without re-keying the queries on every render.
  const configRef = useRef(config);
  configRef.current = config;

  const statuses = pendingRequestIds.map((id) => {
    const status = statusPolling.statusOf(modelType, id);
    return status && { ...status, id };
  });

  // Pull job-history alongside the status polls so the dashboard stays
  // in sync. Stops when there are no in-flight requests.
  const historyEnabled =
    config.jobHistory !== undefined && pendingRequestIds.length > 0;
  const historyQuery = useQuery({
    queryKey: [config.modelType, "cat-history", config.reportId],
    queryFn: async (): Promise<CatJobHistoryResponse> => {
      const c = configRef.current;
      return c.jobHistory ? c.jobHistory.fetch(c.reportId) : { jobs: [] };
    },
    refetchInterval: historyEnabled ? POLL_INTERVAL_MS : false,
    enabled: historyEnabled,
  });

  // Forward history updates upward whenever they arrive.
  useEffect(() => {
    if (historyQuery.data?.jobs) {
      configRef.current.jobHistory?.onUpdate(
        configRef.current.modelType,
        historyQuery.data.jobs
      );
    }
  }, [historyQuery.data]);

  // ``statuses`` is a new array on every render; stash the latest in a ref
  // so the completion effect can read it without taking a dependency on
  // the array reference.
  const statusDataRef = useRef<(StatusWithId | undefined)[]>([]);
  statusDataRef.current = statuses;

  // Transitions false→true exactly once per completion cycle: when the
  // last in-flight status reports complete. After ``setPendingRequestIds([])``
  // it flips back to false.
  const allComplete =
    pendingRequestIds.length > 0 && statuses.every((s) => s?.is_complete);

  useEffect(() => {
    if (!allComplete) return;
    const c = configRef.current;
    const completed = statusDataRef.current.filter(
      (s): s is StatusWithId => s !== undefined
    );
    const anyFailed = completed.some((s) => s.status === WorkStatus.FAILED);

    const finish = async () => {
      if (!anyFailed) {
        await c.onComplete(completed.map((s) => s.id));
        c.toast.show({
          severity: "success",
          summary: `${c.displayName} Requests Complete`,
          detail: `All ${c.displayName} requests have been processed successfully.`,
        });
      } else {
        let detail = `Some ${c.displayName} requests failed. Check run history for details.`;
        if (c.showErrorDetail) {
          const errorDetail = completed.find(
            (s) => s.status === WorkStatus.FAILED && s.error_detail
          )?.error_detail;
          if (errorDetail) {
            detail = `${c.displayName} failed: ${errorDetail}`;
          }
        }
        c.toast.show({
          severity: "warn",
          summary: `${c.displayName} Requests Completed`,
          detail,
          life: c.showErrorDetail ? 8000 : 3000,
        });
      }
      setPendingRequestIds([]);
    };

    finish();
  }, [allComplete, setPendingRequestIds]);

  const applyOutcome = async (response: RunOutcome) => {
    const c = configRef.current;
    if (response.status === "failed") {
      c.toast.show({
        severity: "error",
        summary: `${c.displayName} Request Failed`,
        detail: response.detail,
      });
      return;
    }
    // Already running, or not allowed here: a warning, not a hard error.
    if (response.status !== "started") {
      c.toast.show({
        severity: "warn",
        summary: `${c.displayName} Request`,
        detail: response.detail,
      });
      return;
    }

    // ``unchanged`` means the active batch already reflects the report, so
    // nothing was enqueued — skip polling entirely.
    if (response.action === "unchanged") {
      c.toast.show({
        severity: "info",
        summary: `${c.displayName} Up To Date`,
        detail: `${c.displayName} already up to date — nothing to run.`,
      });
      return;
    }

    if (response.action === "reprice") {
      c.toast.show({
        severity: "info",
        summary: `${c.displayName} Repricing`,
        detail: `${c.displayName} repricing (terms changed).`,
      });
    }

    // ``activate`` (repricing a clearance auto-run shadow) deliberately says
    // nothing, like ``full``: the underwriter never asked for that run, so it
    // must read as an ordinary run that happens to finish quickly.

    setPendingRequestIds([response.request_id]);

    if (c.jobHistory) {
      const history = await c.jobHistory.fetch(c.reportId);
      if (history.jobs) {
        c.jobHistory.onUpdate(c.modelType, history.jobs);
      }
    }
  };

  const handleInitiate = async (activate: boolean, mode?: CatRunMode) => {
    const c = configRef.current;
    setIsInitiateLoading(true);
    try {
      await applyOutcome(await c.initiateRequest(c.reportId, activate, mode));
    } catch {
      c.toast.show({
        severity: "error",
        summary: `${c.displayName} Request Failed`,
        detail: `There was an error initiating your ${c.displayName} request.`,
      });
    } finally {
      setIsInitiateLoading(false);
    }
  };

  return {
    pendingRequestIds,
    setPendingRequestIds,
    isInitiateLoading,
    isPending: pendingRequestIds.length > 0 || isInitiateLoading,
    handleInitiate,
    applyOutcome,
  };
}
